/**
 * La misura del gruppo «nome + damiera + nome», da 900px in su.
 *
 * Il predefinito E' il massimo: la damiera e' il quadrato piu' grande che sta
 * nella banda (`min(100cqw, 100cqh)` in GameScreen.css) e su uno schermo grande
 * deve restare grossa. Questi passi servono solo a SCENDERE, per chi la trova
 * troppo grande: 100 -> 90 -> 80 -> 70 -> 60%. Niente oltre il 100%, e non per
 * prudenza: il tetto `--damiera-max` e' l'inviluppo delle venature cotte
 * (damiera-max.test.ts lo spiega), e la banda porta `contain: size`, quindi un
 * quadrato piu' grande di lei non spingerebbe il layout — finirebbe SOPRA gli
 * avvisi che stanno sotto la damiera.
 *
 * Perche' in localStorage e non nelle preferenze dell'account: la misura giusta
 * dipende dallo SCHERMO che si sta usando, non da chi lo usa — come
 * `draughts-last-ai-difficulty` e compagnia in `last-used-play.ts`. Lo schema
 * delle preferenze del server e' poi `.strict()`: una chiave in piu' li' manda
 * in 400 *ogni* salvataggio di preferenze.
 */
export const BOARD_SCALE_KEY = 'draughts-board-scale';

/** I passi, dal piu' grande al piu' piccolo. Il primo e' il predefinito. */
export const BOARD_SCALE_STEPS = [1, 0.9, 0.8, 0.7, 0.6] as const;

/** Il passo predefinito: la damiera piu' grande che ci sta. */
export const BOARD_SCALE_DEFAULT = BOARD_SCALE_STEPS[0];

/** L'indice del passo, oppure 0 (il predefinito) per qualunque altro valore. */
function indiceDi(valore: number): number {
  const i = BOARD_SCALE_STEPS.findIndex((p) => Math.abs(p - valore) < 0.001);
  return i < 0 ? 0 : i;
}

export function getStoredBoardScale(): number {
  try {
    const grezzo = localStorage.getItem(BOARD_SCALE_KEY);
    if (!grezzo) return BOARD_SCALE_DEFAULT;
    const valore = Number.parseFloat(grezzo);
    if (!Number.isFinite(valore)) return BOARD_SCALE_DEFAULT;
    /* Si torna il PASSO, non il numero letto: un valore fuori elenco (un'altra
       scheda con una versione piu' vecchia, una manina nella console) non deve
       poter dimensionare la damiera con un numero che i bottoni non sanno
       raggiungere — da li' il `+` non risalirebbe. */
    /* `?? ` per `noUncheckedIndexedAccess`: `indiceDi` torna sempre un indice
       valido, ma il tipo non lo sa. */
    return BOARD_SCALE_STEPS[indiceDi(valore)] ?? BOARD_SCALE_DEFAULT;
  } catch {
    return BOARD_SCALE_DEFAULT;
  }
}

export function setStoredBoardScale(valore: number): void {
  try {
    /* Il predefinito si CANCELLA invece di scriverlo: una chiave assente e una
       chiave col valore predefinito vogliono dire la stessa cosa, ed e' lo stile
       di `developer-mode.ts`. */
    if (indiceDi(valore) === 0) localStorage.removeItem(BOARD_SCALE_KEY);
    else localStorage.setItem(BOARD_SCALE_KEY, String(BOARD_SCALE_STEPS[indiceDi(valore)]));
  } catch {
    /* Modalita' privata, quota piena, cookie di terze parti spenti: la misura
       vale per questa sessione e non si ricorda. Non e' un errore da mostrare. */
  }
}

/** Un passo in su (`+1`) o in giu' (`-1`), fermandosi agli estremi. */
export function stepBoardScale(corrente: number, verso: 1 | -1): number {
  /* `verso: 1` e' «piu' grande», cioe' INDIETRO nell'elenco (che e' ordinato dal
     grande al piccolo). */
  const i = indiceDi(corrente) - verso;
  const limitato = Math.min(Math.max(i, 0), BOARD_SCALE_STEPS.length - 1);
  return BOARD_SCALE_STEPS[limitato] ?? BOARD_SCALE_DEFAULT;
}

/** Se da qui si puo' ancora andare in quel verso. Serve al `disabled` dei bottoni. */
export function canStepBoardScale(corrente: number, verso: 1 | -1): boolean {
  return stepBoardScale(corrente, verso) !== BOARD_SCALE_STEPS[indiceDi(corrente)];
}
